import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { IoMdArrowDropdown } from 'react-icons/io';
import { useSelector } from 'react-redux';

import { useQueryClient } from '@tanstack/react-query';
import { Popover, Spin } from 'antd';
import SearchInput from 'components/common/SearchInput/SearchInput';
import { M_SEARCH_CONSTRAINT_QUERY_KEY } from 'constants/queryKeys';
import {
  STEP_SELECTOR_CANCEL_LABEL,
  STEP_SELECTOR_FETCHING_LABEL,
  STEP_SELECTOR_SAVE_LABEL,
  STEP_SELECTOR_SEARCH_PLACEHOLDER,
  TASKS_GRID_TARGET_ADD_PLACEHOLDER,
  TASKS_GRID_TARGET_FIELD_LABEL,
} from 'constants/uiConstants';
import type { IItem } from 'interfaces/IInput';
import MergeClassNames from 'utils/MergeClassNames';

import { selectActiveGOrgId } from '../../../../redux/selectors/GlobalCatalogueSelectors';

import CatalogueOptionList from './CatalogueOptionList';
import SelectionChips from './SelectionChips';
import {
  getStepOptionId,
  stepOptionsMatch,
  StepSelectorOption,
} from './stepConfig';
import { summariseTargets } from './targetSummary';
import { useConstraintCatalogue } from './useConstraintCatalogue';

interface TargetSpeciesPickerProps {
  value: IItem[];
  onCommit: (items: IItem[]) => void;
}

// Converts a StepSelectorOption back to IItem for persisting to the task.
const toIItem = (option: StepSelectorOption): IItem => ({
  item: option.item,
  globalId: option.globalId ?? null,
});

const toOption = (item: IItem): StepSelectorOption => ({
  label: item.item,
  item: item.item,
  globalId: item.globalId ?? null,
});

// Target, reason or pest for an application, as a multi-select dropdown
// (OP-2534): the list opens with the current targets ticked, clicking a row
// ticks or unticks it, and Save replaces the task's targets with the ticked
// set. Replaces the earlier chip list with an append-only "+ Add" picker.
const TargetSpeciesPicker: React.FC<TargetSpeciesPickerProps> = ({
  value,
  onCommit,
}) => {
  const gOrgId = useSelector(selectActiveGOrgId);
  const queryClient = useQueryClient();

  const {
    constraintOptions,
    isLoading,
    isSearchFetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    hasFetchNextPageError,
    searchTerm,
    onSearch,
    clearSearch,
  } = useConstraintCatalogue(gOrgId);

  const [isOpen, setIsOpen] = useState(false);
  const [checkedItems, setCheckedItems] = useState<StepSelectorOption[]>([]);

  // Purge MSearch cache when this picker unmounts so a fresh re-mount
  // always starts with current data (mirrors StepInputSelector's own cleanup).
  useEffect(() => {
    return () => {
      queryClient.removeQueries({
        queryKey: [M_SEARCH_CONSTRAINT_QUERY_KEY],
      });
    };
  }, [queryClient]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        setCheckedItems(value.map(toOption));
        clearSearch();
      }
      setIsOpen(open);
    },
    [clearSearch, value]
  );

  const checkedGlobalIds = useMemo(
    () =>
      new Set(
        checkedItems.map((i) => i.globalId).filter((id): id is string => !!id)
      ),
    [checkedItems]
  );

  const toggleCheckedItem = useCallback((item: StepSelectorOption) => {
    setCheckedItems((prev) => {
      const exists = prev.some((existing) => stepOptionsMatch(existing, item));
      return exists
        ? prev.filter((existing) => !stepOptionsMatch(existing, item))
        : [...prev, item];
    });
  }, []);

  const handleRemoveChecked = useCallback((id: string) => {
    setCheckedItems((prev) => prev.filter((i) => getStepOptionId(i) !== id));
  }, []);

  const handleSave = useCallback(() => {
    onCommit(checkedItems.map(toIItem));
    setIsOpen(false);
  }, [checkedItems, onCommit]);

  const handleCancel = useCallback(() => {
    setIsOpen(false);
  }, []);

  const summary = summariseTargets(value);

  const overlayContent = (
    <div className="flex h-[calc(100vh-12rem)] w-96 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg">
      <div className="flex-shrink-0 p-2">
        <SearchInput
          value={searchTerm}
          onChange={onSearch}
          placeholder={STEP_SELECTOR_SEARCH_PLACEHOLDER}
        />
      </div>

      {isSearchFetching && (
        <div className="flex flex-shrink-0 items-center gap-1.5 border-b border-gray-100 px-3 py-1.5 text-xs text-gray-muted">
          <Spin size="small" />
          <span>{STEP_SELECTOR_FETCHING_LABEL}</span>
        </div>
      )}

      {checkedItems.length > 0 && (
        <div className="flex-shrink-0 px-2 pb-1">
          <SelectionChips
            items={checkedItems}
            onRemoveItem={handleRemoveChecked}
          />
        </div>
      )}

      <div
        className={MergeClassNames(
          'flex-1 overflow-hidden',
          isLoading && 'flex items-center justify-center'
        )}
      >
        {isLoading ? (
          <Spin size="small" />
        ) : (
          <CatalogueOptionList
            key="constraint"
            items={constraintOptions}
            selectedGlobalId={null}
            onSelectItem={toggleCheckedItem}
            hasNextPage={hasNextPage}
            fetchNextPage={fetchNextPage}
            isFetchingNextPage={isFetchingNextPage}
            hasFetchNextPageError={hasFetchNextPageError}
            isMultiSelectMode
            checkedGlobalIds={checkedGlobalIds}
            compact
          />
        )}
      </div>

      <div className="flex flex-shrink-0 items-center justify-end gap-2 border-t border-gray-200 px-3 py-2">
        <button
          type="button"
          className="rounded-[var(--radius-button)] border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-dark"
          onClick={handleCancel}
        >
          {STEP_SELECTOR_CANCEL_LABEL}
        </button>
        <button
          type="button"
          className="rounded-[var(--radius-button)] bg-success-dark px-3 py-1.5 text-sm font-semibold text-white"
          onClick={handleSave}
        >
          {STEP_SELECTOR_SAVE_LABEL}
        </button>
      </div>
    </div>
  );

  return (
    <Popover
      open={isOpen}
      onOpenChange={handleOpenChange}
      content={overlayContent}
      trigger="click"
      placement="bottomLeft"
      destroyOnHidden
      classNames={{ root: 'target-species-picker-popover' }}
    >
      <button
        type="button"
        aria-label={TASKS_GRID_TARGET_FIELD_LABEL}
        title={value.map((item) => item.item).join(', ') || undefined}
        className="flex h-7 w-48 min-w-0 items-center gap-1 rounded border border-solid border-gray-argent bg-white px-2 text-left text-sm hover:border-success"
      >
        <span
          className={MergeClassNames(
            'min-w-0 flex-1 truncate',
            summary ? 'text-charcoal' : 'text-gray-muted'
          )}
        >
          {summary || TASKS_GRID_TARGET_ADD_PLACEHOLDER}
        </span>
        <IoMdArrowDropdown className="flex-shrink-0 text-gray-dark" />
      </button>
    </Popover>
  );
};

export default TargetSpeciesPicker;
